/**
 * /apps/:appId/policies — list of HR policies grouped by status.
 *
 * View-first: each policy renders as a card. Click any card to open the
 * full-page editor at /apps/:appId/policies/:policyId.
 *
 * "+ New policy" creates a draft with just a title and immediately
 * redirects to its detail page so the body can be filled in there with
 * room to breathe.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';

const STATUS_GROUPS = [
  { value: 'active',  label: 'Active'  },
  { value: 'draft',   label: 'Draft'   },
  { value: 'retired', label: 'Retired' },
];

export default function Policies() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { canWrite } = useWorkspace();

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.policies.list(appId);
      setPolicies(r.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, appId]);

  useEffect(() => { load(); }, [load]);

  // Group by status for display order; 'active' first.
  const grouped = {};
  for (const p of policies) {
    const k = p.status || 'draft';
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(p);
  }

  return (
    <div className="policies-page">
      <div className="entity-head">
        <h1 className="page-title">HR Policies</h1>
        {canWrite && (
          <button className="btn-primary" onClick={() => setCreateOpen(true)}>
            + New policy
          </button>
        )}
      </div>
      <p className="page-subtitle">
        Long-form HR documents: leave policy, code of conduct, remote-work
        guidelines, compensation principles, and similar. Each policy has a
        draft → active → retired lifecycle.
      </p>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <p className="muted">Loading policies…</p>
      ) : policies.length === 0 ? (
        <div className="empty-state-small">
          No policies yet.
          {canWrite && ' Click "+ New policy" to draft your first one.'}
        </div>
      ) : (
        STATUS_GROUPS.map((g) => grouped[g.value] && grouped[g.value].length > 0 && (
          <section key={g.value} className="policies-group">
            <h2 className="policies-group-title">
              {g.label}
              <span className="muted small"> · {grouped[g.value].length}</span>
            </h2>
            <div className="policies-grid">
              {grouped[g.value].map((p) => (
                <PolicyCard
                  key={p.id}
                  policy={p}
                  onClick={() => navigate(`/apps/${appId}/policies/${p.id}`)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <CreatePolicyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(p) => navigate(`/apps/${appId}/policies/${p.id}`)}
      />
    </div>
  );
}

function PolicyCard({ policy, onClick }) {
  return (
    <button type="button" className="policy-card" onClick={onClick}>
      <div className="policy-card-top">
        <span className={`status-pill status-${policy.status}`}>{policy.status}</span>
        {policy.category && <span className="policy-category">{policy.category}</span>}
      </div>
      <h3 className="policy-card-title">{policy.title}</h3>
      <p className="muted small">
        v{policy.version}
        {policy.effective_from && ` · effective ${formatDate(policy.effective_from)}`}
      </p>
    </button>
  );
}

function CreatePolicyModal({ open, onClose, onCreated }) {
  const { appId } = useParams();
  const api = useApi();
  const [title, setTitle]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);

  React.useEffect(() => {
    if (open) { setTitle(''); setError(null); setSubmitting(false); }
  }, [open]);

  const handle = async (e) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) { setError('Title is required'); return; }
    setSubmitting(true);
    try {
      const created = await api.policies.create(appId, { title: title.trim() });
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create');
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="New Policy" onClose={submitting ? undefined : onClose}>
      <form onSubmit={handle} className="entity-form">
        <label className="form-label">
          <span>Title <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          <input
            type="text"
            className="form-input"
            placeholder="e.g. Remote Work Policy"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            autoFocus
          />
          <small className="form-help">
            You'll add the body, category, status and effective date on the
            next page.
          </small>
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Creating…' : 'Continue to editor'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
